import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Users, Search, Eye, Trash2, Key, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UserManagementPage = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Create user form
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    full_name: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/admin/users${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (username) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    fetchUserDetails(user.username);
    setShowDetailsDialog(true);
  };

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${tokenToDelete.username}/tokens/${tokenToDelete.tokenId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Token deleted successfully');
      setShowDeleteDialog(false);
      setTokenToDelete(null);
      fetchUsers();
      if (selectedUser) {
        fetchUserDetails(selectedUser.username);
      }
    } catch (error) {
      console.error('Failed to delete token:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete token');
    }
  };

  const openDeleteDialog = (username, tokenId, tokenName) => {
    setTokenToDelete({ username, tokenId, tokenName });
    setShowDeleteDialog(true);
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Password length check
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        newUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User created successfully');
      setShowCreateDialog(false);
      resetNewUserForm();
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const resetNewUserForm = () => {
    setNewUser({
      username: '',
      email: '',
      password: '',
      full_name: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-8 w-8 text-slate-700" />
              <h1 className="text-3xl font-outfit font-bold text-slate-900">{t('admin.user_management.title')}</h1>
            </div>
            <p className="text-slate-600 font-ibm">{t('admin.user_management.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-emerald hover:bg-emerald/90 font-ibm"
          >
            <Users className="mr-2 h-4 w-4" />
            {t('admin.user_management.create_user')}
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder={t('admin.user_management.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 font-ibm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">User Accounts</CardTitle>
          <CardDescription className="font-ibm">
            {filteredUsers.length} user account(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 font-ibm">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('admin.user_management.no_users')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.username}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="font-ibm font-semibold text-lg text-slate-900">
                          {user.username}
                        </h3>
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 font-ibm">
                          <Key className="h-3 w-3 mr-1" />
                          {user.total_tokens} token(s)
                        </Badge>
                        {user.active_tokens > 0 && (
                          <Badge className="bg-green-100 text-green-800 border-green-200 font-ibm">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {user.active_tokens} active
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 font-ibm">
                        <div>
                          <span className="font-medium">Last Login:</span>{' '}
                          {formatDate(user.last_login)}
                        </div>
                        <div>
                          <span className="font-medium">Total Logins:</span> {user.total_logins}
                        </div>
                      </div>
                      
                      {/* Token List Preview */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {user.tokens.slice(0, 3).map((token) => (
                          <Badge
                            key={token.id}
                            variant="outline"
                            className="font-ibm text-xs"
                          >
                            {token.name}
                          </Badge>
                        ))}
                        {user.tokens.length > 3 && (
                          <Badge variant="outline" className="font-ibm text-xs">
                            +{user.tokens.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(user)}
                        className="font-ibm"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {selectedUser?.username}
            </DialogTitle>
            <DialogDescription className="font-ibm">
              Complete user information and token details
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
            </div>
          ) : userDetails ? (
            <div className="space-y-6 mt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-slate-600 font-ibm">Total Tokens</p>
                  <p className="text-2xl font-outfit font-bold">{userDetails.total_tokens}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-slate-600 font-ibm">Active Tokens</p>
                  <p className="text-2xl font-outfit font-bold">{userDetails.active_tokens}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-slate-600 font-ibm">Total Logins</p>
                  <p className="text-2xl font-outfit font-bold">{userDetails.total_logins}</p>
                </Card>
              </div>

              {/* Tokens List */}
              <div>
                <h3 className="font-ibm font-semibold text-lg mb-3">Tokens</h3>
                <div className="space-y-3">
                  {userDetails.tokens.map((token) => (
                    <div
                      key={token.id}
                      className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-ibm font-medium">{token.name}</h4>
                            <Badge
                              className={`font-ibm ${
                                token.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {token.status === 'active' ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )}
                              {token.status}
                            </Badge>
                            <Badge variant="outline" className="font-ibm">
                              {token.token_type}
                            </Badge>
                          </div>
                          <div className="text-sm text-slate-600 font-ibm">
                            <p>Created: {formatDate(token.created_at)}</p>
                            {token.last_login && (
                              <p>Last Login: {formatDate(token.last_login)}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(userDetails.username, token.id, token.name)}
                          className="text-red-600 hover:text-red-700 font-ibm"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Login History */}
              {userDetails.login_history && userDetails.login_history.length > 0 && (
                <div>
                  <h3 className="font-ibm font-semibold text-lg mb-3">Recent Login History</h3>
                  <div className="space-y-2">
                    {userDetails.login_history.map((login, index) => (
                      <div
                        key={index}
                        className="border border-slate-200 rounded p-3 text-sm font-ibm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-500" />
                            <span>{formatDate(login.timestamp)}</span>
                          </div>
                          <Badge
                            className={`${
                              login.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {login.status}
                          </Badge>
                        </div>
                        {login.ip_address && (
                          <p className="text-xs text-slate-600 mt-1">IP: {login.ip_address}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Token Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">Delete Token?</AlertDialogTitle>
            <AlertDialogDescription className="font-ibm">
              Are you sure you want to delete the token <strong>{tokenToDelete?.tokenName}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-ibm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteToken}
              className="bg-red-600 hover:bg-red-700 font-ibm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetNewUserForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Create New User</DialogTitle>
            <DialogDescription className="font-ibm">
              Add a new user account to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="new-username" className="text-sm font-ibm font-medium">
                Username *
              </label>
              <Input
                id="new-username"
                value={newUser.username}
                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                placeholder="Enter username"
                className="font-ibm"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="new-email" className="text-sm font-ibm font-medium">
                Email *
              </label>
              <Input
                id="new-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="user@example.com"
                className="font-ibm"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm font-ibm font-medium">
                Password *
              </label>
              <Input
                id="new-password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Minimum 6 characters"
                className="font-ibm"
              />
              <p className="text-xs text-slate-500 font-ibm">
                Password must be at least 6 characters long
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="new-fullname" className="text-sm font-ibm font-medium">
                Full Name
              </label>
              <Input
                id="new-fullname"
                value={newUser.full_name}
                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                placeholder="John Doe"
                className="font-ibm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetNewUserForm();
              }}
              className="flex-1 font-ibm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creating}
              className="flex-1 bg-emerald hover:bg-emerald/90 font-ibm"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
